import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { brl, dateBR } from "@/lib/format";
import { Shield, Building2, DollarSign, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { ResetDemoDataButton } from "@/components/ResetDemoData";

export function AdminLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: loc.state?.email || "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await adminApi.post("/admin/login", f); localStorage.setItem("pg_admin_token", r.data.token); nav("/admin-master"); }
    catch { toast.error("Credenciais inválidas"); }
  };
  return (
    <div className="admin-dark min-h-screen bg-zinc-950 grid place-items-center p-6">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 grid place-items-center"><Shield className="h-5 w-5 text-zinc-950" /></div>
            <div><div className="font-display text-xl font-bold text-white">Admin Master</div><div className="text-xs text-emerald-400">Portal Gestão</div></div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-zinc-300">E-mail</Label><Input className="bg-zinc-950 border-zinc-800 text-white" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email" /></div>
            <div><Label className="text-zinc-300">Senha</Label><Input type="password" className="bg-zinc-950 border-zinc-800 text-white" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password" /></div>
            <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-zinc-950" data-testid="admin-submit">Entrar</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const KPIA = ({ icon: I, label, value, tone = "emerald" }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardContent className="p-6">
      <I className={`h-5 w-5 text-${tone}-400 mb-2`} />
      <div className="text-xs uppercase text-zinc-500 font-semibold">{label}</div>
      <div className="font-mono-num text-3xl font-extrabold text-white mt-1">{value}</div>
    </CardContent>
  </Card>
);

export function AdminDashboard() {
  const [d, setD] = useState(null);
  const load = () => adminApi.get("/admin/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold">Dashboard SaaS</h1>
        <ResetDemoDataButton onDone={load} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPIA icon={DollarSign} label="MRR" value={brl(d?.mrr)} />
        <KPIA icon={TrendingUp} label="ARR" value={brl(d?.arr)} />
        <KPIA icon={Building2} label="Empresas" value={d?.total_tenants ?? 0} />
        <KPIA icon={Users} label="Ativas" value={d?.active ?? 0} />
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        {["free","essencial","profissional","master"].map((p) => (
          <Card key={p} className="bg-zinc-900 border-zinc-800"><CardContent className="p-5">
            <div className="text-xs uppercase text-zinc-500 font-semibold capitalize">{p}</div>
            <div className="font-mono-num text-2xl font-extrabold text-white">{d?.by_plan?.[p] ?? 0}</div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

export function AdminTenants() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => setList((await adminApi.get("/admin/tenants" + (q ? `?q=${q}` : ""))).data);
  useEffect(() => { load(); }, [q]);
  const change = async (id, updates) => { await adminApi.patch(`/admin/tenants/${id}`, updates); toast.success("Atualizado"); load(); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold">Empresas</h1>
        <Input placeholder="Buscar..." className="max-w-xs bg-zinc-900 border-zinc-800 text-white" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
      </div>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-zinc-500 text-xs uppercase bg-zinc-950"><tr>
            <th className="text-left px-4 py-3">Empresa</th><th className="text-left px-4 py-3">Plano</th>
            <th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Cadastro</th>
            <th className="text-right px-4 py-3">Ações</th></tr></thead>
          <tbody>{list.map((r) => (
            <tr key={r.id} className="border-t border-zinc-800" data-testid={`admin-tenant-${r.id}`}>
              <td className="px-4 py-3 text-white font-medium">{r.name}<div className="text-xs text-zinc-500">/{r.slug}</div></td>
              <td className="px-4 py-3"><select value={r.plan} onChange={(e) => change(r.id, { plan: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-emerald-400" data-testid={`tenant-plan-${r.id}`}>{["free", "essencial", "profissional", "master"].map((p) => <option key={p} value={p}>{p}</option>)}</select></td>
              <td className="px-4 py-3"><select value={r.subscription_status} onChange={(e) => change(r.id, { status: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-300" data-testid={`tenant-status-${r.id}`}>{["trial", "active", "past_due", "canceled", "blocked", "pending"].map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
              <td className="px-4 py-3 text-zinc-500">{dateBR(r.created_at)}</td>
              <td className="px-4 py-3 text-right space-x-1">
                <Button size="sm" variant="outline" className="border-zinc-700 bg-zinc-950 text-white hover:bg-zinc-800" onClick={() => change(r.id, { extend_trial_days: 7 })} data-testid={`extend-${r.id}`}>+7d trial</Button>
                <Button size="sm" variant="outline" className="border-zinc-700 bg-zinc-950 text-white hover:bg-zinc-800" onClick={() => change(r.id, { status: "active", plan: r.plan === "free" ? "essencial" : r.plan })} data-testid={`release-${r.id}`}>Liberar</Button>
                <Button size="sm" variant="outline" className={`border-zinc-700 bg-zinc-950 hover:bg-zinc-800 ${r.subscription_status === "blocked" ? "text-emerald-400" : "text-rose-400"}`} onClick={() => change(r.id, { status: r.subscription_status === "blocked" ? "active" : "blocked" })} data-testid={`block-${r.id}`}>{r.subscription_status === "blocked" ? "Desbloquear" : "Bloquear"}</Button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

export function AdminPlans() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get("/admin/plans").then((r) => { setList(r.data); setEdit(Object.fromEntries(r.data.map((p) => [p.id, { price: p.price, price_yearly: p.price_yearly ?? "" }]))); });
  useEffect(() => { load(); }, []);
  const dirty = list.some((p) => parseFloat(edit[p.id]?.price) !== p.price || (edit[p.id]?.price_yearly !== "" && parseFloat(edit[p.id]?.price_yearly) !== (p.price_yearly ?? null)));
  const confirm = async () => {
    setSaving(true);
    try {
      for (const p of list) {
        const e = edit[p.id]; const body = {};
        if (parseFloat(e.price) !== p.price && !isNaN(parseFloat(e.price))) body.price = parseFloat(e.price);
        if (e.price_yearly !== "" && parseFloat(e.price_yearly) !== p.price_yearly) body.price_yearly = parseFloat(e.price_yearly);
        if (Object.keys(body).length) await adminApi.patch(`/admin/plans/${p.id}`, body);
      }
      toast.success("Preços confirmados. O Portal Gestão já exibe os novos valores."); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setSaving(false);
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-extrabold">Planos</h1><p className="text-sm text-zinc-400">Os preços confirmados aqui valem imediatamente na landing, no cadastro, na assinatura e nas cobranças (cartão e PIX).</p></div>
        <Button onClick={confirm} disabled={!dirty || saving} className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950" data-testid="plans-confirm">{saving ? "Salvando..." : "Confirmar preços"}</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {list.map((p) => (
          <Card key={p.id} className={`bg-zinc-900 border-zinc-800 ${parseFloat(edit[p.id]?.price) !== p.price ? "ring-1 ring-amber-500" : ""}`}><CardContent className="p-6">
            <div className="text-xs uppercase text-emerald-400 font-semibold">{p.id}</div>
            <div className="font-display text-2xl font-extrabold text-white mt-1">{p.name}</div>
            <div className="mt-4"><Label className="text-zinc-400">Preço mensal (R$)</Label>
              <Input type="number" step="0.01" value={edit[p.id]?.price ?? ""} onChange={(e) => setEdit({ ...edit, [p.id]: { ...edit[p.id], price: e.target.value } })} className="bg-zinc-950 border-zinc-800 text-white mt-1" data-testid={`plan-price-${p.id}`} /></div>
            {p.id !== "free" && <div className="mt-3"><Label className="text-zinc-400">Preço anual PIX (R$)</Label>
              <Input type="number" step="0.01" value={edit[p.id]?.price_yearly ?? ""} onChange={(e) => setEdit({ ...edit, [p.id]: { ...edit[p.id], price_yearly: e.target.value } })} className="bg-zinc-950 border-zinc-800 text-white mt-1" data-testid={`plan-yearly-${p.id}`} /></div>}
            <div className="text-xs text-zinc-500 mt-3">Atual: {brl(p.price)}/mês · {p.features?.length || 0} recursos</div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

export function AdminCoupons() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ code: "", kind: "percent", value: 10 });
  const load = async () => setList((await adminApi.get("/admin/coupons")).data);
  useEffect(() => { load(); }, []);
  const save = async () => { await adminApi.post("/admin/coupons", { ...f, value: parseFloat(f.value) }); toast.success("Cupom criado"); setF({ code: "", kind: "percent", value: 10 }); load(); };
  const del = async (id) => { await adminApi.delete(`/admin/coupons/${id}`); load(); };
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-extrabold">Cupons</h1>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-6 flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[120px]"><Label className="text-zinc-400">Código</Label><Input className="bg-zinc-950 border-zinc-800 text-white" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="coupon-code" /></div>
        <div><Label className="text-zinc-400">Tipo</Label>
          <select className="h-10 rounded-md bg-zinc-950 border border-zinc-800 text-white px-3 text-sm" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option value="percent">%</option><option value="fixed">R$</option></select></div>
        <div><Label className="text-zinc-400">Valor</Label><Input type="number" className="bg-zinc-950 border-zinc-800 text-white" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></div>
        <Button className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950" onClick={save} data-testid="coupon-save">Criar</Button>
      </CardContent></Card>
      <div className="grid md:grid-cols-3 gap-3">{list.map((c) => (
        <Card key={c.id} className="bg-zinc-900 border-zinc-800"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="font-mono-num font-bold text-white">{c.code}</div>
            <div className="text-xs text-zinc-500">{c.kind === "percent" ? `${c.value}%` : brl(c.value)}</div></div>
          <Button size="sm" variant="outline" className="border-zinc-700 bg-zinc-950 text-white" onClick={() => del(c.id)}>Excluir</Button>
        </CardContent></Card>
      ))}</div>
    </div>
  );
}

export function AdminSupport() {
  const [list, setList] = useState([]);
  useEffect(() => { adminApi.get("/admin/tickets").then((r) => setList(r.data)); }, []);
  const reply = async (t) => {
    const msg = prompt("Responder:"); if (!msg) return;
    await adminApi.post(`/admin/tickets/${t.id}/reply`, { message: msg, status: "respondido" });
    toast.success("Enviada");
    setList((await adminApi.get("/admin/tickets")).data);
  };
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-extrabold">Suporte</h1>
      {list.length === 0 && <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-8 text-center text-zinc-500">Nenhum chamado.</CardContent></Card>}
      {list.map((t) => (
        <Card key={t.id} className="bg-zinc-900 border-zinc-800"><CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1"><div className="text-xs text-emerald-400 font-semibold uppercase">{t.restaurant_name}</div>
              <div className="font-display font-bold text-white mt-1">{t.subject}</div>
              <div className="text-zinc-400 text-sm mt-1">{t.message}</div>
              <div className="text-xs text-zinc-600 mt-2">Status: {t.status} • {dateBR(t.created_at)}</div>
            </div>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950" onClick={() => reply(t)}>Responder</Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

export function AdminGateway() {
  const [gw, setGw] = useState(null);
  const [access, setAccess] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => { const r = await adminApi.get("/admin/gateway"); setGw(r.data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch("/admin/gateway", {
        access_token: access || undefined,
        webhook_secret: secret || undefined,
      });
      toast.success("Configuração salva");
      setAccess(""); setSecret(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };
  const copyUrl = () => { if (gw?.webhook_url) { navigator.clipboard.writeText(gw.webhook_url); toast.success("URL copiada"); } };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Pagamentos</h1>
        <p className="text-zinc-400 mt-1">Configure o gateway Mercado Pago que cobra as assinaturas dos restaurantes.</p>
      </div>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-6 space-y-4" data-testid="gw-status">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-xs uppercase text-zinc-500 font-semibold">Status do gateway</div>
            <div className={`font-mono-num text-2xl font-extrabold ${gw?.configured ? "text-emerald-400" : "text-rose-400"}`}>
              {gw?.configured ? "Ativo" : "Não configurado"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 font-semibold">Assinatura de webhook</div>
            <div className={`font-mono-num text-2xl font-extrabold ${gw?.webhook_configured ? "text-emerald-400" : "text-amber-400"}`}>
              {gw?.webhook_configured ? "OK" : "Faltando"}
            </div>
          </div>
        </div>
      </CardContent></Card>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Credenciais Mercado Pago</h3>
        <div className="text-xs text-zinc-500">Valores em uso: <span className="font-mono-num text-zinc-300">Token: {gw?.access_token_masked || "—"}</span> <span className="mx-2">•</span> <span className="font-mono-num text-zinc-300">Webhook: {gw?.webhook_secret_masked || "—"}</span></div>
        <div>
          <Label className="text-zinc-300">Access Token de Produção</Label>
          <Input type="password" placeholder="APP_USR-... (deixe vazio para manter o atual)"
            className="bg-zinc-950 border-zinc-800 text-white font-mono-num mt-1"
            value={access} onChange={(e) => setAccess(e.target.value)} data-testid="gw-access-token" />
          <div className="text-xs text-zinc-500 mt-1">Obtenha em <span className="text-emerald-400">Mercado Pago → Desenvolvedores → Suas integrações → Credenciais</span></div>
        </div>
        <div>
          <Label className="text-zinc-300">Chave secreta de webhook</Label>
          <Input type="password" placeholder="whsec_... (deixe vazio para manter o atual)"
            className="bg-zinc-950 border-zinc-800 text-white font-mono-num mt-1"
            value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="gw-webhook-secret" />
          <div className="text-xs text-zinc-500 mt-1">Copie a chave após criar o webhook no painel do Mercado Pago.</div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950" data-testid="gw-save">
          {saving ? "Salvando..." : "Salvar credenciais"}
        </Button>
      </CardContent></Card>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-6 space-y-3">
        <h3 className="font-display font-bold text-lg">URL do webhook</h3>
        <p className="text-sm text-zinc-400">Cadastre esta URL no painel do Mercado Pago para receber eventos de pagamento e assinatura:</p>
        <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <span className="flex-1 text-sm font-mono-num text-emerald-300 truncate">{gw?.webhook_url}</span>
          <Button size="sm" variant="outline" className="border-zinc-700 bg-zinc-950 text-white" onClick={copyUrl} data-testid="gw-copy">Copiar</Button>
        </div>
        <p className="text-xs text-zinc-500">Eventos assinados são obrigatórios — requisições sem <code>X-Signature</code> válida são rejeitadas com 401.</p>
      </CardContent></Card>
    </div>
  );
}

export function AdminAudit() {
  const [list, setList] = useState([]);
  useEffect(() => { adminApi.get("/admin/audit-logs").then((r) => setList(r.data)); }, []);
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-extrabold">Auditoria</h1>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-zinc-500 text-xs uppercase bg-zinc-950"><tr>
            <th className="text-left px-4 py-3">Quando</th><th className="text-left px-4 py-3">Ação</th><th className="text-left px-4 py-3">Motivo</th></tr></thead>
          <tbody>{list.map((l) => (
            <tr key={l.id} className="border-t border-zinc-800"><td className="px-4 py-3 text-zinc-400">{dateBR(l.at)}</td>
              <td className="px-4 py-3 text-white">{l.kind}</td><td className="px-4 py-3 text-zinc-500">{l.reason || "—"}</td></tr>
          ))}</tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
